import { Component, Input, OnDestroy, inject } from '@angular/core';
import { AbstractControl, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Subject } from 'rxjs/internal/Subject';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';
import { EContactType, IContactUsFormValue } from 'src/app/contact-us/contact-us.interface';
import { ContactUsService } from 'src/app/contact-us/contact-us.service';
import { EAlertBoxType, IAlertBoxConfig } from '../alert-box/alert-box.interface';
import { ESectionColor } from '../sections/section.interface';
import { GoogleAnalyticsService } from 'src/app/core/google-analytics/google-analytics.service';
import { AlertBoxComponent } from '../alert-box/alert-box.component';

@Component({
    selector: 'app-contact-us',
    templateUrl: './contact-us.component.html',
    styleUrl: './contact-us.component.scss',
    imports: [
        ReactiveFormsModule,
        AlertBoxComponent,
    ]
})
export class ContactUsComponent implements OnDestroy {
  protected readonly _formBuilder: FormBuilder = inject(FormBuilder);
  protected readonly _contactUsService: ContactUsService = inject(ContactUsService);
  protected readonly _analyticsService: GoogleAnalyticsService = inject(GoogleAnalyticsService);

  @Input() public color: ESectionColor = ESectionColor.BLUE;
  @Input() public bookDemo: Boolean = false;

  private readonly _destroy$: Subject<boolean> = new Subject<boolean>();

  public readonly alertBoxType: typeof EAlertBoxType = EAlertBoxType;

  public alertBoxConfig!: Partial<IAlertBoxConfig>;
  public isSuccessfullySubmitted: boolean | null = null;
  public form = this._formBuilder.group({
    fullName: ['', Validators.required],
    phoneNumber: ['', Validators.required],
    email: ['', [Validators.required, Validators.email]],
    organization: ['', Validators.required],
    message: ['', Validators.required],
    newsletterOptIn: [false],
  });

  get fullName(): AbstractControl<string | null, string | null> | null {
    return this.form.get('fullName');
  }
  get phoneNumber(): AbstractControl<string | null, string | null> | null {
    return this.form.get('phoneNumber');
  }
  get email(): AbstractControl<string | null, string | null> | null {
    return this.form.get('email');
  }
  get organization(): AbstractControl<string | null, string | null> | null {
    return this.form.get('organization');
  }
  get message(): AbstractControl<string | null, string | null> | null {
    return this.form.get('message');
  }
  get newsletterOptIn(): AbstractControl<boolean | null, boolean | null> | null {
    return this.form.get('newsletterOptIn');
  }
  get contacType(): AbstractControl<EContactType | null, EContactType | null> | null {
    return this.form.get('contacType');
  }

  public ngOnDestroy(): void {
    this._destroy$.next(true);
    this._destroy$.unsubscribe();
  }

  public getSubmitBtnColor(): string {
    switch (this.color) {
      case ESectionColor.BLUE:
      case ESectionColor.RED:
        return 'secondary';

      default:
        return 'primary';
    }
  }

  public submit(): void {
    if (!this.form.valid) {
      return;
    }

    this._contactUsService
      .postContactUsForm(
        { data: this.form.getRawValue() as unknown as IContactUsFormValue },
        this.bookDemo ? EContactType.DEMO_REQUEST : EContactType.QUOTE_SAMPLE_PERSONALIZATION,
      )
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: this._submitOnSuccess.bind(this),
        error: this._submitOnError.bind(this),
      });
  }

  private _submitOnSuccess(data: Partial<IContactUsFormValue>): void {
    this.isSuccessfullySubmitted = true;
    this.alertBoxConfig = {
      title: 'Success',
      message: `
        Thank you for reaching out! Your contact request has been successfully
        submitted and we will get back to you as soon as possible. Have a great day!
      `,
    };

    this._clearForm();
    this._analyticsService.event('generate_lead');
  }

  private _submitOnError(error: any): void {
    this.isSuccessfullySubmitted = false;
    this.alertBoxConfig = {
      title: 'Error',
      message: `
        An error occured while submitting your contact request. Please try submitting
        the form again.
      `,
    };

    console.log(error);
  }

  public dismissAlertBox() {
    this.isSuccessfullySubmitted = null;
  }

  private _clearForm() {
    const contacType: AbstractControl<EContactType | null, EContactType | null> | null = this.form.get('contacType');

    this.form.reset();

    contacType?.patchValue(this.bookDemo ? EContactType.DEMO_REQUEST : EContactType.QUOTE_SAMPLE_PERSONALIZATION);
  }
}
